import axios from 'axios';
import { HttpRequester as requester } from '../https/HttpRequester';

// Types
import { FilterableTypes } from '../types/FilterableTypes';

const generateSingleFilterQuery = (prefix: string, filter: any): string[] => {
  const keys = Object.keys(filter);
  const propName = keys[0];
  const value = filter[propName];

  let result: string[] = [
    `${prefix}.fieldName=${propName}`,
    `${prefix}.fieldValue=${value}`,
    `${prefix}.filterType=${filter.filterType || FilterableTypes.Equals}`,
  ];

  if (filter.isInverted) {
    result.push(`${prefix}.isInverted=true`);
  }

  if (filter.and) {
    const andResult = generateSingleFilterQuery(`${prefix}.and`, filter.and);

    result = [...result, ...andResult];
  }
  if (filter.or) {
    const orResult = generateSingleFilterQuery(`${prefix}.or`, filter.or);

    result = [...result, ...orResult];
  }

  return result;
};

const generateFilterQueryString = (filters: any, option: string) => {
  if (!Array.isArray(filters)) return '';

  const queryStringArray = filters.flatMap((filter, index) => {
    if (typeof filter !== 'object') {
      return [];
    }

    const keys = Object.keys(filter);
    const propName = keys[0];
    const value = filter[propName];

    if (!propName || value == undefined) return [];

    if (option === 'filters') {
      return generateSingleFilterQuery(`filters[${index}]`, filter);
    }
    if (option === 'sortBy') {
      return [`sort[${index}].Key=${propName}`, `sort[${index}].Value=${value}`];
    }
  });
  return queryStringArray.join('&');
};

const buildParams = (url, params) => {
  let urlPath = '';
  //if (url) urlPath += `${apiVersion}/${url}`;
  if (url) urlPath += `${url}`;
  if (params !== undefined) {
    const paramKeys = Object.keys(params);
    if (paramKeys.length !== 0) {
      urlPath += '?';
    }

    return (urlPath += paramKeys
      .filter((v) => params[v] !== null && params[v] !== undefined)
      .map((key) => {
        if (key === 'filters' || key === 'sortBy') {
          return generateFilterQueryString(params[key], key);
        } else {
          return `${encodeURIComponent(key)}=${encodeURIComponent(params[key].toString())}`;
        }
      })
      .join('&'));
  }
  // if we got this far, return empty string
  return urlPath;
};

// TODO: these methods are not good. I'm slowly replacing them with _New variants as the opportunity arises,
// which throw errors when the response is not successful instead. - Melle

export const httpPost = async <T>(url: string, model: any = undefined): Promise<T> => await requester.post(url, model);

export const httpPostUnauthenticated = async <T>(url: string, model: any = undefined): Promise<T> => await requester.postUnauthenticated(url, model);

export const httpPut = async <T>(url: string, model: any = undefined): Promise<T> => await requester.put(url, model);

export const httpPatch = async <T>(url: string, model: any = undefined): Promise<T> => await requester.patch(url, model);

export const httpDelete = async <T>(url: string, payload?: any): Promise<T> => {
  return await requester.delete(url, payload);
};

export const httpGet = async <T>(url: string, model: any = undefined): Promise<T> => await requester.get(buildParams(url, model));

export const uploadFiles = async <T>(url: string, model: any = undefined, onUploadProgress: (progressEvent) => void): Promise<T> =>
  await requester.uploadFile(url, model, onUploadProgress);

export const httpGetFile = async (url: string, model: any = undefined, fileName?: string): Promise<void> =>
  requester.getFile(buildParams(url, model), fileName);

export const httpGetBlob = async (url: string, model: any = undefined): Promise<Blob> => {
  const config = await requester.getConfig();

  const response = await axios.get(buildParams(url, model), {
    ...config,
    responseType: 'blob',
  });

  return response.data;
};

// download PDFs
export const downloadPdfFile = async (url: string, fileName: string): Promise<void> => {
  try {
    const config = await requester.getConfig();

    const response = await axios.get(url, {
      ...config,
      responseType: 'blob',
    });

    const blob = new Blob([response.data], { type: 'application/pdf' });
    const blobUrl = window.URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = fileName.endsWith('.pdf') ? fileName : `${fileName}.pdf`;
    document.body.appendChild(link);
    link.click();
    link.remove();

    window.URL.revokeObjectURL(blobUrl);
  } catch (error) {
    console.error('PDF download failed:', error);
    throw error;
  }
};

/**
 * New method that throws error on failure and doesn't wrap response in SingleResult
 */
export const httpGet_New = async <T>(url: string, model: any = undefined): Promise<T> => await requester.get_New(buildParams(url, model));

/**
 * New method that throws error on failure and doesn't wrap response in SingleResult
 */
export const httpDelete_New = async <T>(url: string): Promise<T> => await requester.delete_New(url);

/**
 * New method that throws error on failure and doesn't wrap response in SingleResult
 */
export const httpPost_New = async <T>(url: string, model: any = undefined, isFormData = false): Promise<T> =>
  await requester.post_New(url, model, isFormData);

/**
 * New method that throws error on failure and doesn't wrap response in SingleResult
 */
export const httpPut_New = async <T>(url: string, model: any = undefined, isFormData = false): Promise<T> =>
  await requester.put_New(url, model, isFormData);

/**
 * New method that throws error on failure and doesn't wrap response in SingleResult
 */
export const httpPatch_New = async <T>(url: string, model: any = undefined, isFormData = false): Promise<T> =>
  await requester.patch_New(url, model, isFormData);
